import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Key, Copy, Eye, EyeOff, AlertTriangle, Search, RefreshCw, User, Wallet } from 'lucide-react';

const AdminWalletKeys = () => {
    const navigate = useNavigate();
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleKeys, setVisibleKeys] = useState({});
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        fetchWalletKeys();
    }, []);

    const fetchWalletKeys = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/wallet-keys', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Access denied. Super Admin access required.');
                }
                throw new Error('Failed to fetch wallet keys');
            }

            const data = await response.json();
            setWallets(data.wallets || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleKeyVisibility = (id) => {
        setVisibleKeys(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    const filteredWallets = wallets.filter(wallet =>
        wallet.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.network?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.token?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-900">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Key className="w-8 h-8 text-red-500" />
                        <h1 className="text-3xl font-bold text-white">Wallet Private Keys</h1>
                    </div>
                    <p className="text-gray-400">View all user wallet addresses and their private keys</p>
                </div>

                {/* Warning Banner */}
                <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                        <div>
                            <p className="text-red-400 font-semibold">⚠️ SENSITIVE DATA - HANDLE WITH EXTREME CARE</p>
                            <p className="text-red-400/80 text-sm">Private keys provide full control over wallet funds. Never share or expose these keys.</p>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search by address, email, network, or token..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                        />
                    </div>
                    <button
                        onClick={fetchWalletKeys}
                        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>

                {/* Stats */}
                <div className="bg-gray-800 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-yellow-500" />
                            <span className="text-gray-400">Total Wallets:</span>
                            <span className="text-white font-semibold">{wallets.length}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400">Showing:</span>
                            <span className="text-white font-semibold">{filteredWallets.length}</span>
                        </div>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-900/30 border border-red-500 rounded-xl p-4 mb-6">
                        <p className="text-red-400">{error}</p>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin" />
                    </div>
                )}

                {/* Wallet List */}
                {!loading && (
                    <div className="space-y-4">
                        {filteredWallets.length === 0 ? (
                            <div className="bg-gray-800 rounded-xl p-8 text-center">
                                <p className="text-gray-400">No wallets found</p>
                            </div>
                        ) : (
                            filteredWallets.map((wallet) => (
                                <div key={wallet.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                                    {/* Header Row */}
                                    <div className="flex flex-wrap items-center gap-4 mb-4">
                                        <span className="bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full text-sm font-medium">
                                            {wallet.network}
                                        </span>
                                        <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
                                            {wallet.token}
                                        </span>
                                        {wallet.user && (
                                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                                                <User className="w-4 h-4" />
                                                <span>{wallet.user.email}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Address */}
                                    <div className="mb-4">
                                        <label className="text-gray-400 text-sm mb-1 block">Public Address</label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 bg-gray-900 px-4 py-2 rounded-lg text-green-400 font-mono text-sm break-all">
                                                {wallet.address}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(wallet.address, `addr-${wallet.id}`)}
                                                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                                                title="Copy Address"
                                            >
                                                <Copy className={`w-4 h-4 ${copiedId === `addr-${wallet.id}` ? 'text-green-500' : 'text-gray-400'}`} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Private Key */}
                                    <div>
                                        <label className="text-gray-400 text-sm mb-1 block">Private Key</label>
                                        {wallet.decryptError ? (
                                            <div className="bg-red-900/20 border border-red-500/30 px-4 py-2 rounded-lg">
                                                <p className="text-red-400 text-sm">❌ Decrypt Error: {wallet.decryptError}</p>
                                            </div>
                                        ) : wallet.privateKey ? (
                                            <div className="flex items-center gap-2">
                                                <code className="flex-1 bg-gray-900 px-4 py-2 rounded-lg text-red-400 font-mono text-sm break-all">
                                                    {visibleKeys[wallet.id] ? wallet.privateKey : '••••••••••••••••••••••••••••••••••••••••••••••••'}
                                                </code>
                                                <button
                                                    onClick={() => toggleKeyVisibility(wallet.id)}
                                                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                                                    title={visibleKeys[wallet.id] ? 'Hide Key' : 'Show Key'}
                                                >
                                                    {visibleKeys[wallet.id] ? (
                                                        <EyeOff className="w-4 h-4 text-gray-400" />
                                                    ) : (
                                                        <Eye className="w-4 h-4 text-gray-400" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => copyToClipboard(wallet.privateKey, `key-${wallet.id}`)}
                                                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                                                    title="Copy Private Key"
                                                >
                                                    <Copy className={`w-4 h-4 ${copiedId === `key-${wallet.id}` ? 'text-green-500' : 'text-gray-400'}`} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bg-gray-900 px-4 py-2 rounded-lg">
                                                <p className="text-gray-500 text-sm">No private key stored</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between text-sm text-gray-500">
                                        <span>Created: {new Date(wallet.createdAt).toLocaleString()}</span>
                                        <span>{wallet.networkName}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminWalletKeys;
